/**
 * Alert sounds for the Communications module.
 *
 * Two shapes: a one-shot tone for an arriving notification, and a LOOPING
 * ringtone that runs for as long as a call is ringing. Files live in
 * `public/audio/<tone>.mp3` — the stems double as the wire values of the
 * `message_tone` / `call_ringtone` preferences, and the server validates
 * against the same list (`services/notifications.js` → ALERT_TONES).
 *
 * Autoplay: browsers refuse `play()` until the document has been interacted
 * with, and the failure is a rejected promise, not an exception — every call
 * here swallows it. `primeAudio()` spends the FIRST user gesture unlocking the
 * SELECTED tones (muted play → stop), which is what makes a later notification
 * arriving on an idle tab actually audible. Safari unlocks per element, not
 * per page, so picking a different tone must prime that one too —
 * `primeTone()` exists for the settings dropdown, whose change handler is
 * itself a gesture. Inside the dashboard iframe all of this works because the
 * embed grants `allow="autoplay"`.
 *
 * Priming is a play we want to HAPPEN and never want to HEAR, and getting that
 * wrong is audible rather than merely wrong: a `play()` the browser has not
 * honoured yet will start on its own once the media finishes loading, and if
 * the element was unmuted in the meantime the tone rings out seconds after the
 * click that queued it, with nothing on screen to explain it. Safari defers
 * that load past the gesture, so it is where this bites. Three rules keep it
 * impossible, and all three are load-bearing:
 *
 *   - An element is MUTED whenever nothing intentionally wants it heard, and
 *     the play paths unmute for themselves rather than trusting a prime to
 *     have left it unmuted (which also stops a prime in flight from swallowing
 *     the tone an agent pressed Play to hear).
 *   - [intents] records who asked for the playback an element is doing, and a
 *     `play` event nobody asked for is stopped inside its own handler.
 *   - A tone is primed ONCE. Two primes racing on one element is how the
 *     element ends up unmuted with a request still outstanding.
 */

export type AlertTone = 'ding' | 'bell' | 'chime' | 'magic' | 'ping' | 'ringtone';

export const ALERT_TONES: { value: AlertTone; label: string }[] = [
  { value: 'ding', label: 'Ding' },
  { value: 'bell', label: 'Bell' },
  { value: 'chime', label: 'Chime' },
  { value: 'magic', label: 'Magic' },
  { value: 'ping', label: 'Ping' },
  { value: 'ringtone', label: 'Ringtone' },
];

export const DEFAULT_MESSAGE_TONE: AlertTone = 'ding';
export const DEFAULT_CALL_RINGTONE: AlertTone = 'ringtone';

const TONE_VALUES = new Set<string>(ALERT_TONES.map((tone) => tone.value));

export const isAlertTone = (value: unknown): value is AlertTone =>
  typeof value === 'string' && TONE_VALUES.has(value);

const toneUrl = (tone: AlertTone) => `${import.meta.env.BASE_URL}audio/${tone}.mp3`;

/**
 * Who asked for the playback an element is doing. `none` is the resting state
 * and the one that matters: playback starting while nothing wants it is a
 * request the browser queued and honoured late, and it is silenced on sight.
 */
type Intent = 'none' | 'prime' | 'play' | 'ring';

/** One element per tone, reused — re-creating one per play leaks decoders. */
const elements = new Map<AlertTone, HTMLAudioElement>();
const intents = new Map<AlertTone, Intent>();
/** Tones whose unlock has already been spent; priming one twice is the race. */
const primed = new Set<AlertTone>();

const intentOf = (tone: AlertTone): Intent => intents.get(tone) ?? 'none';

/** Back to rest: stopped, rewound, and muted so a stray play is inaudible. */
function idle(audio: HTMLAudioElement, tone: AlertTone): void {
  intents.set(tone, 'none');
  try {
    audio.pause();
    audio.loop = false;
    audio.currentTime = 0;
    audio.muted = true;
  } catch {
    // A detached element or a mid-load seek can throw synchronously; silence
    // is the whole point here and must not take the caller down with it.
  }
}

const elementFor = (tone: AlertTone): HTMLAudioElement | null => {
  if (typeof Audio === 'undefined') return null;
  const existing = elements.get(tone);
  if (existing) return existing;
  const audio = new Audio(toneUrl(tone));
  audio.preload = 'auto';
  // Muted until something asks for it out loud — see the module docblock.
  audio.muted = true;
  elements.set(tone, audio);

  // The `play` EVENT, not the promise, is the browser saying playback has
  // actually begun: the promise resolves on its own schedule, and can reject
  // for a play that then starts anyway once the media has loaded.
  audio.addEventListener('play', () => {
    const intent = intentOf(tone);
    if (intent === 'prime') {
      // The unlock is spent the moment playback starts — end it here rather
      // than waiting for the promise, which may be several seconds behind.
      primed.add(tone);
      idle(audio, tone);
      return;
    }
    // Nobody asked. This is the late-honoured request, and stopping it here is
    // what keeps a tone from playing at random over an agent's dashboard.
    if (intent === 'none') idle(audio, tone);
  });

  // A one-shot that finished is at rest again; a ringtone loops and never ends.
  audio.addEventListener('ended', () => {
    if (intentOf(tone) === 'play') idle(audio, tone);
  });

  return audio;
};

let primeHooked = false;

/**
 * Spend a user gesture unlocking one element. Muted, stopped the instant it
 * starts, and skipped when the tone is already unlocked or busy — a second
 * prime racing the first is what leaves an element unmuted with a play request
 * still outstanding.
 */
export function primeTone(tone: AlertTone): void {
  if (primed.has(tone) || intentOf(tone) !== 'none') return;
  const audio = elementFor(tone);
  if (!audio) return;
  intents.set(tone, 'prime');
  try {
    audio.muted = true;
    void audio
      .play()
      .then(() => {
        // Belt and braces for a browser that resolved without firing `play`.
        if (intentOf(tone) !== 'prime') return;
        primed.add(tone);
        idle(audio, tone);
      })
      .catch(() => {
        // Refused. The element stays muted and UNPRIMED so the next gesture
        // tries again — unmuting here is precisely what made a rejected-then-
        // honoured play audible.
        if (intentOf(tone) === 'prime') intents.set(tone, 'none');
      });
  } catch {
    intents.set(tone, 'none');
  }
}

/**
 * Unlock playback on the first user gesture. Idempotent, and safe to call
 * before the preferences have loaded — [getTones] is read AT gesture time, so
 * it sees whatever the agent has selected by then. Only those tones are
 * fetched: priming all six would pull ~400 KB for sounds most agents never
 * choose.
 */
export function primeAudio(getTones: () => AlertTone[]): void {
  if (primeHooked || typeof document === 'undefined') return;
  primeHooked = true;

  const events = ['pointerdown', 'keydown', 'touchstart'] as const;
  const unlock = () => {
    // Every listener goes on the first gesture, not just the one that fired:
    // `{ once: true }` is per EVENT TYPE, so a click and then a keystroke used
    // to prime the same elements twice over.
    for (const event of events) document.removeEventListener(event, unlock);
    for (const tone of new Set(getTones())) primeTone(tone);
  };

  for (const event of events) document.addEventListener(event, unlock, { passive: true });
}

/** Fire a tone once. No-ops when the browser refuses — never throws. */
export function playTone(tone: AlertTone, volume = 0.6): void {
  const audio = elementFor(tone);
  if (!audio) return;
  try {
    intents.set(tone, 'play');
    audio.loop = false;
    // Unmuted HERE rather than left unmuted by the prime: an element only ever
    // audible while something wants it heard cannot surprise anyone.
    audio.muted = false;
    audio.volume = volume;
    audio.currentTime = 0;
    void audio.play().catch(() => {
      // Guarded on `paused`: a rejection that arrives after playback started
      // anyway must not stop the tone it is reporting on.
      if (audio.paused) idle(audio, tone);
    });
  } catch {
    // A detached element or a mid-load seek can throw synchronously; a missed
    // alert tone must never take the toast down with it.
    idle(audio, tone);
  }
}

let ringing: HTMLAudioElement | null = null;
let ringingTone: AlertTone | null = null;

/**
 * Loop `tone` until [stopRinging]. Calling it again with the SAME tone while
 * already ringing is a no-op — a second ring arriving must not restart the
 * loop from zero, or a stack of calls stutters the ringtone.
 */
export function startRinging(tone: AlertTone, volume = 0.5): void {
  const audio = elementFor(tone);
  if (!audio) return;
  if (ringing === audio && !audio.paused) return;
  stopRinging();
  try {
    intents.set(tone, 'ring');
    audio.loop = true;
    audio.muted = false;
    audio.volume = volume;
    audio.currentTime = 0;
    ringing = audio;
    ringingTone = tone;
    void audio.play().catch(() => {
      if (!audio.paused) return;
      if (ringing === audio) {
        ringing = null;
        ringingTone = null;
      }
      idle(audio, tone);
    });
  } catch {
    ringing = null;
    ringingTone = null;
    idle(audio, tone);
  }
}

export function stopRinging(): void {
  const audio = ringing;
  const tone = ringingTone;
  ringing = null;
  ringingTone = null;
  if (!audio || !tone) return;
  idle(audio, tone);
}
